import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import {
  Elements,
  AddressElement,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useCart } from './CartContext';
import { apiRequest, loginRequest } from './authConfig';
import { SALES_TAX_API_ADDRESS, PAYMENTS_API_ADDRESS } from './apiConfig';
import { stripePromise } from './stripeConfig';
import { termLabel, termCadence, usd } from './priceFormat';
import './hierarchy.css';

// SalesTax and Payments return OkObjectResult(jsonString), so the body is a JSON
// string that needs a second parse — the same double-encoding as the Products API.
async function readDoubleEncoded(res) {
  const payload = await res.json();
  return typeof payload === 'string' ? JSON.parse(payload) : payload;
}

// The store back end (StoreAPIService) authenticates the caller from their Entra
// token, so every call carries a Bearer access token for the API scope. Silent
// acquisition falls back to a redirect when interaction is required.
async function acquireApiToken(instance, accounts) {
  const account = instance.getActiveAccount() || accounts[0];
  if (!account) throw new Error('No signed-in account.');
  try {
    const { accessToken } = await instance.acquireTokenSilent({
      scopes: apiRequest.scopes,
      account,
    });
    return accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await instance.acquireTokenRedirect({ scopes: apiRequest.scopes, account });
    }
    throw error;
  }
}

// The card form and address collection live inside <Elements>, so this inner
// component is where checkout is actually driven. It owns the tax calculation,
// the order placement, and the payment confirmation.
function CheckoutForm({ item, unitPrice, priceId, email, onTotalChange, onPlaced }) {
  const stripe = useStripe();
  const elements = useElements();
  const { instance, accounts } = useMsal();

  const [company, setCompany] = useState('');
  const [taxId, setTaxId] = useState('');
  // Billing name + address come from Stripe's Address Element, which reports its
  // completeness and current value on change.
  const [address, setAddress] = useState(null);
  const [addressComplete, setAddressComplete] = useState(false);
  // Stripe's Payment Element reports its own completeness; card details are a
  // required field too, so we track it to gate the Place order button.
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Tax is calculated server-side by the SalesTax endpoint (Stripe Tax needs the
  // secret key). taxState drives the summary: idle → loading → ready | unavailable.
  // The returned CalculationId is passed on to the Payments step.
  const [tax, setTax] = useState(null);
  const [taxState, setTaxState] = useState('idle');
  const [calculationId, setCalculationId] = useState(null);

  const [placing, setPlacing] = useState(false);
  const [placeError, setPlaceError] = useState(null);

  // Recalculate tax whenever the completed billing address — or the priced line —
  // changes. Guarded on a complete address so we only call with a full address
  // Stripe Tax can use, and debounced so editing after completion doesn't fire a
  // paid tax calculation on every keystroke. The resolved total (incl. tax) is
  // lifted to the parent so the deferred PaymentElement's amount matches what the
  // subscription's first invoice will charge.
  useEffect(() => {
    if (!priceId || !addressComplete || !address?.address) {
      setTax(null);
      setCalculationId(null);
      setTaxState('idle');
      onTotalChange(null);
      return;
    }

    let cancelled = false;
    setTaxState('loading');
    const timer = setTimeout(async () => {
      try {
        const accessToken = await acquireApiToken(instance, accounts);
        const res = await fetch(SALES_TAX_API_ADDRESS, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            ProductId: item.productId,
            PriceId: priceId,
            Address: address.address,
          }),
        });
        if (!res.ok) throw new Error(`Tax calculation failed (${res.status})`);
        const data = await readDoubleEncoded(res);
        if (cancelled) return;
        if (typeof data?.TaxAmount === 'number' && typeof data?.TotalAmount === 'number') {
          setTax(data.TaxAmount);
          setCalculationId(data.CalculationId || null);
          setTaxState('ready');
          onTotalChange(data.TotalAmount);
        } else {
          setTax(null);
          setCalculationId(null);
          setTaxState('unavailable');
          onTotalChange(null);
        }
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) return; // redirecting for consent
        if (cancelled) return;
        setTax(null);
        setCalculationId(null);
        setTaxState('unavailable');
        onTotalChange(null);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [priceId, addressComplete, address, item.productId, instance, accounts, onTotalChange]);

  // A payment method that redirects for authentication (e.g. 3-D Secure) returns
  // the buyer to return_url with redirect_status. Reflect that outcome once here.
  useEffect(() => {
    const status = new URLSearchParams(window.location.search).get('redirect_status');
    if (status === 'succeeded') {
      onPlaced();
    } else if (status === 'failed') {
      setPlaceError('Your payment could not be completed. Please try again.');
    }
    // Runs once on mount to read the redirect result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addressInvalid = submitted && !addressComplete;
  const agreedInvalid = submitted && !agreed;

  // Place order is enabled only when every required field is entered (billing
  // address + payment details), tax has been calculated for that address, and
  // the buyer has agreed to the terms — and Stripe.js is ready to confirm.
  const canPlaceOrder =
    addressComplete &&
    paymentComplete &&
    taxState === 'ready' &&
    agreed &&
    !!stripe &&
    !!elements;

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitted(true);
    setPlaceError(null);
    if (!addressComplete || !agreed) return;
    // The deferred PaymentElement validates its amount against the subscription's
    // first invoice at confirmation, so we need the tax-inclusive total first.
    if (taxState !== 'ready') {
      setPlaceError(
        'We couldn’t calculate tax for this billing address. Please check it and try again.'
      );
      return;
    }
    if (!stripe || !elements) return;

    setPlacing(true);
    try {
      // Validate + collect the payment details before creating anything server-side.
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setPlaceError(submitError.message || 'Please check your payment details.');
        setPlacing(false);
        return;
      }

      // Create the Stripe customer + subscription server-side. The back end
      // re-prices from our database (never trusts a client amount), so we send
      // identifiers, not money. It returns the first invoice's PaymentIntent
      // client secret for us to confirm.
      const accessToken = await acquireApiToken(instance, accounts);
      const res = await fetch(PAYMENTS_API_ADDRESS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          ProductId: item.productId,
          PriceId: priceId,
          CalculationId: calculationId,
          BillingDetails: { Name: address.name, Email: email, Address: address.address },
          Company: company.trim() || null,
          TaxId: taxId.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(`Payment setup failed (${res.status})`);
      const data = await readDoubleEncoded(res);
      const clientSecret = data?.ClientSecret;
      if (!clientSecret) throw new Error('No client secret returned.');

      // Confirm the payment with Stripe.js. Card details never touch our servers;
      // redirect only if the payment method requires it (e.g. 3-D Secure).
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: { return_url: `${window.location.origin}/checkout` },
        redirect: 'if_required',
      });
      if (error) {
        setPlaceError(error.message || 'Your payment could not be completed.');
        setPlacing(false);
        return;
      }

      // Confirmed without a redirect — the subscription is active and the order is
      // written by the Stripe webhook. Clear the cart and show confirmation.
      onPlaced();
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) return; // redirecting for consent
      setPlaceError('Something went wrong placing your order. Please try again.');
      setPlacing(false);
    }
  };

  return (
    <form className="page" onSubmit={handleSubmit} noValidate>
      <p>
        <Link className="text-link" to="/cart">← Back to cart</Link>
      </p>
      <h1 className="page-title">Checkout</h1>

      <section className="item-group">
        <h2 className="item-group-title">Order Summary</h2>
        <div className="item-stack">
          <article className="item-card cart-line">
            <div className="cart-line-body">
              <span className="item-title cart-line-title">{item.productName}</span>
              <p className="cart-line-meta">
                {termLabel(item.term)} · {item.seatCount} seat{item.seatCount === 1 ? '' : 's'}
              </p>
            </div>
            <div className="cart-line-price">{usd.format(unitPrice)}</div>
          </article>
        </div>

        <div className="cart-summary">
          <div className="cart-summary-lines">
            <div className="cart-summary-row">
              <span className="cart-summary-label">Subtotal</span>
              <span>{usd.format(unitPrice)}</span>
            </div>
            <div className="cart-summary-row">
              <span className="cart-summary-label">Tax</span>
              <span className="cart-summary-tax">
                {taxState === 'ready'
                  ? usd.format(tax)
                  : taxState === 'loading'
                  ? 'Calculating…'
                  : taxState === 'unavailable'
                  ? 'Unavailable — check address'
                  : 'Enter billing address'}
              </span>
            </div>
            <div className="cart-summary-row">
              <span className="cart-summary-label">Due now</span>
              <span className="cart-summary-total">
                {taxState === 'ready' ? (
                  usd.format(unitPrice + tax)
                ) : (
                  <>{usd.format(unitPrice)} <span className="cart-summary-tax">+ tax</span></>
                )}
              </span>
            </div>
            <p className="note">
              Then billed {termCadence(item.term)} at {usd.format(unitPrice)} + tax
            </p>
            <p className="note note--italic">No commitment — cancel your subscription anytime.</p>
            <p className="note note--italic">Tax calculated from your billing address.</p>
          </div>
        </div>
      </section>

      <section className="item-group">
        <h2 className="item-group-title">Billing details</h2>
        <div className="checkout-form">
          <label className="form-field">
            <span className="form-label">License holder</span>
            <span className="form-readonly">{email}</span>
            <span className="note">
              Your subscription and seats are assigned to this account.
            </span>
          </label>

          <label className="form-field">
            <span className="form-label">Company / organization</span>
            <input
              className="input"
              type="text"
              autoComplete="organization"
              value={company}
              onChange={e => setCompany(e.target.value)}
            />
          </label>

          <div className={`form-field${addressInvalid ? ' form-field--invalid' : ''}`}>
            <span className="form-label">
              Billing name &amp; address<span className="form-required">*</span>
            </span>
            <AddressElement
              options={{ mode: 'billing' }}
              onChange={event => {
                setAddressComplete(event.complete);
                setAddress(event.value);
              }}
            />
            {addressInvalid && (
              <span className="note note--error">
                Please complete your billing name and address.
              </span>
            )}
          </div>

          <label className="form-field">
            <span className="form-label">Tax ID / VAT number</span>
            <input
              className="input"
              type="text"
              value={taxId}
              onChange={e => setTaxId(e.target.value)}
            />
            <span className="note">Optional — for business purchases.</span>
          </label>
        </div>
      </section>

      <section className="item-group">
        <h2 className="item-group-title">Payment</h2>
        {/* Card details are collected by Stripe's Payment Element (Stripe.js) and
            confirmed directly with Stripe — they never reach Nuvolem's servers. */}
        <div className="checkout-payment">
          <PaymentElement onChange={event => setPaymentComplete(event.complete)} />
        </div>
      </section>

      <label className={`checkout-consent${agreedInvalid ? ' checkout-consent--invalid' : ''}`}>
        <input
          type="checkbox"
          checked={agreed}
          onChange={e => setAgreed(e.target.checked)}
        />
        <span>
          I agree to the{' '}
          <Link className="text-link" to="/terms">Terms of Service</Link> and{' '}
          <Link className="text-link" to="/privacy">Privacy Policy</Link>, and I
          understand this is a recurring subscription billed{' '}
          {termCadence(item.term)} (plus applicable tax) until cancelled.
        </span>
      </label>

      {placeError && (
        <p className="note note--error checkout-note">{placeError}</p>
      )}

      <div className="cart-actions">
        <button
          type="submit"
          className="btn btn--filled"
          disabled={placing || !canPlaceOrder}
        >
          {placing ? 'Placing order…' : 'Place order'}
        </button>
      </div>
    </form>
  );
}

function CheckoutPage() {
  const { item, unitPrice, clear } = useCart();
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  const account = instance.getActiveAccount() || accounts[0] || null;
  // The license binds to the signed-in account, so its email is the billing
  // email — shown read-only rather than re-collected.
  const email = account?.username || '';

  const [placed, setPlaced] = useState(false);
  // Total incl. tax reported by the SalesTax step, used to seed the deferred
  // PaymentElement's amount so Stripe displays and validates the right figure.
  const [total, setTotal] = useState(null);

  // Each cart price carries its database Id (the PriceId the store API validates
  // against), so the selected line resolves to a price id without another fetch.
  const priceId = item
    ? item.prices.find(p => p.Term === item.term && p.SeatCount === item.seatCount)?.Id ?? null
    : null;

  const amountCents = Math.round((total ?? unitPrice) * 100);
  const elementsOptions = useMemo(
    () => ({ mode: 'subscription', amount: amountCents, currency: 'usd' }),
    [amountCents]
  );

  // Order placed — show confirmation even though the cart is now cleared.
  if (placed) {
    return (
      <div className="page">
        <h1 className="page-title">Order placed</h1>
        <p className="empty-text">
          Thank you{email ? `, ${email}` : ''}. Your subscription is being
          activated and a receipt has been emailed to you. Your license and seats
          will appear in License Management shortly.
        </p>
        <div className="cart-actions cart-actions--center">
          <Link className="btn btn--filled" to="/admin">Manage licenses</Link>
          <Link className="btn btn--outlined" to="/products">Continue shopping</Link>
        </div>
      </div>
    );
  }

  // Nothing to check out — send the user back to the cart.
  if (!item) return <Navigate to="/cart" replace />;

  // Checkout requires a signed-in account — prompt sign-in before showing the
  // order. The login redirect otherwise lands the user on the home page (see
  // navigateToLoginRequestUrl: false in authConfig), so we pass the return path
  // through MSAL's `state`; App restores it once the redirect resolves. The
  // cart is persisted, so the order is still here when they come back.
  if (!isAuthenticated) {
    return (
      <div className="page">
        <p>
          <Link className="text-link" to="/cart">← Back to cart</Link>
        </p>
        <h1 className="page-title">Checkout</h1>
        <p className="empty-text">Please sign in to complete your order.</p>
        <div className="cart-actions cart-actions--center">
          <button
            type="button"
            className="btn btn--filled"
            onClick={() => instance.loginRedirect({ ...loginRequest, state: '/checkout' })}
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="page">
        <p>
          <Link className="text-link" to="/cart">← Back to cart</Link>
        </p>
        <h1 className="page-title">Checkout</h1>
        <p className="checkout-payment">
          Checkout is unavailable — Stripe isn’t configured. Set
          REACT_APP_STRIPE_PUBLISHABLE_KEY to enable it.
        </p>
      </div>
    );
  }

  const handlePlaced = () => {
    clear();
    setPlaced(true);
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <CheckoutForm
        item={item}
        unitPrice={unitPrice}
        priceId={priceId}
        email={email}
        onTotalChange={setTotal}
        onPlaced={handlePlaced}
      />
    </Elements>
  );
}

export default CheckoutPage;
